import { getApiWriteHeaders, getTweetApiBaseUrl } from './apiBase';

export interface ApiHealth {
  ok: boolean;
  service?: string;
  supabase?: boolean;
  rpc?: boolean;
  rpcEndpoints?: number;
  rpcLatencyMs?: number | null;
  supabaseLatencyMs?: number | null;
  rateLimitStore?: 'redis' | 'memory';
  reconciler?: {
    lastProcessedBlock: number | null;
    lastHeartbeatAt: string | null;
    heartbeatAgeSeconds: number | null;
    version: string | null;
    workerInstanceId?: string | null;
    workerStartedAt?: string | null;
  } | null;
  ts?: number;
  /** Round-trip latency measured by the browser (ms). */
  roundTripMs: number;
  /** True when the API server responded at all (even with 503). */
  reachable: boolean;
  /** Set when reachable=false (timeout, CORS, missing VITE_API_URL, etc.). */
  failureReason?: string;
}

/**
 * Probe the API server's /api/health. Returns reachable=false (never throws)
 * when the server is down / unreachable so the monitor can render a status pill.
 */
function describeApiHealthFailure(base: string, err: unknown): string {
  if (!import.meta.env.DEV && !base) {
    return 'VITE_API_URL missing in this build';
  }
  if (import.meta.env.DEV && !base) {
    return 'local API not running — run npm run server (port 3001)';
  }
  if (err instanceof DOMException && err.name === 'AbortError') {
    return 'request timed out';
  }
  return 'network or CORS — check Railway deploy and CORS_ORIGINS';
}

export async function fetchApiHealth(timeoutMs = 8000): Promise<ApiHealth> {
  const base = getTweetApiBaseUrl();
  if (!import.meta.env.DEV && !base) {
    return { ok: false, roundTripMs: 0, reachable: false, failureReason: 'VITE_API_URL missing in this build' };
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const t0 = performance.now();
  try {
    const res = await fetch(`${base}/api/health`, { method: 'GET', signal: controller.signal });
    const roundTripMs = Math.round(performance.now() - t0);
    const body = (await res.json().catch(() => ({}))) as Partial<ApiHealth>;
    return { ...body, ok: !!body.ok, roundTripMs, reachable: true } as ApiHealth;
  } catch (err) {
    return {
      ok: false,
      roundTripMs: Math.round(performance.now() - t0),
      reachable: false,
      failureReason: describeApiHealthFailure(base, err),
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fire-and-forget: tell the API that a wallet connected the app. The server
 * upserts one row per (UTC day, wallet) so this is safe to call repeatedly, but
 * callers should still throttle (see WalletConnectTracker) to avoid noise.
 */
export async function postWalletConnect(address: string): Promise<void> {
  const wallet = (address || '').trim().toLowerCase();
  if (!/^0x[a-f0-9]{40}$/.test(wallet)) return;
  try {
    const base = getTweetApiBaseUrl();
    await fetch(`${base}/api/wallet/connect`, {
      method: 'POST',
      headers: getApiWriteHeaders(),
      body: JSON.stringify({ wallet }),
    });
  } catch {
    // Analytics is best-effort; never surface connect-tracking failures to the user.
  }
}

export interface HealthSamplePayload {
  chain_head?: number | null;
  reconciler_block?: number | null;
  block_lag?: number | null;
  pending_votes?: number | null;
  stale_pending_votes?: number | null;
  rpc_latency_ms?: number | null;
  heartbeat_age_secs?: number | null;
}

/** Persist a health sample server-side (best-effort; requires API_WRITE_KEY). */
export async function postHealthSample(sample: HealthSamplePayload): Promise<boolean> {
  try {
    const base = getTweetApiBaseUrl();
    const res = await fetch(`${base}/api/admin/health-sample`, {
      method: 'POST',
      headers: getApiWriteHeaders(),
      body: JSON.stringify(sample),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export interface ServerHealthHistoryRow {
  sampled_at: string;
  chain_head: number | null;
  reconciler_block: number | null;
  block_lag: number | null;
  pending_votes: number | null;
  stale_pending_votes: number | null;
  rpc_latency_ms: number | null;
  heartbeat_age_secs: number | null;
}
